import { Search, SendHorizontal, UserCircle2 } from "lucide-react";
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHead,
  TableRow,
  Textarea,
  useToast,
} from "randmarcomps";
import {
  ReturnOld as ApiReturn,
  useGetV4WarehousesQuery,
  usePostV4ResellerByRouteResellerIdReturnAndReturnNumberAddCommentMutation,
} from "randmar-api-client";
import ManufacturerContext from "../../contexts/ManufacturerContext";
import Path from "../../routes/Path";
import Return from "../../stores/return/Return";
import ReturnStatusPill from "../returns/ReturnStatusPill";
import InlineSpinner from "../util/InlineSpinner";

type ParsedComment = {
  message: string;
  meta: string;
};

interface Props {
  returns?: (Return | ApiReturn)[];
  defaultRowsPerPage?: number;
}

const rowsPerPageOptions = [10, 20, 50, 100] as const;

const isLegacyReturn = (ret: Return | ApiReturn): ret is Return => "returnNumber" in ret;

const convertLegacyReturnToApiReturn = (legacyReturn: Return): ApiReturn => {
  const {
    returnNumber,
    resellerId,
    status,
    invoiceNumber,
    requestDate,
    creditNumber,
    contactName,
    contactPhone,
    contactEmail,
    comments,
    mpn,
    quantity,
    resellerName,
    warehouseLocation,
  } = legacyReturn;

  return {
    ReturnNumber: returnNumber,
    ResellerId: resellerId ? String(resellerId) : undefined,
    Status: status,
    InvoiceNumber: invoiceNumber,
    RequestDate: requestDate,
    CreditNumber: creditNumber,
    ContactName: contactName,
    ContactPhone: contactPhone,
    ContactEmail: contactEmail,
    Comments: comments,
    RandmarSKU: mpn,
    Quantity: quantity,
    ResellerName: resellerName,
    WarehouseLocation: warehouseLocation,
  };
};

const getReturnMpn = (row: ApiReturn): string => {
  const returnRecord = row as unknown as Record<string, unknown>;
  if (typeof returnRecord.MPN === "string" && returnRecord.MPN.trim() !== "") {
    return returnRecord.MPN;
  }
  if (typeof returnRecord.mpn === "string" && returnRecord.mpn.trim() !== "") {
    return returnRecord.mpn;
  }
  return row.RandmarSKU || "-";
};

const ReturnGrid: React.FC<Props> = ({ returns, defaultRowsPerPage }) => {
  const { applicationId } = useContext(ManufacturerContext);
  const [localReturns, setLocalReturns] = useState<ApiReturn[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage || 20);
  const [isCommentsDialogOpen, setCommentsDialogOpen] = useState(false);
  const [selectedComments, setSelectedComments] = useState<ParsedComment[]>([]);
  const [selectedReturn, setSelectedReturn] = useState<ApiReturn | null>(null);
  const [newComment, setNewComment] = useState("");
  const { toast } = useToast();
  const lastCommentRef = useRef<HTMLDivElement>(null);

  const { data: warehouses } = useGetV4WarehousesQuery();
  const [addComment, { isLoading: isAddingComment }] = usePostV4ResellerByRouteResellerIdReturnAndReturnNumberAddCommentMutation();

  useEffect(() => {
    if (!returns) {
      setLocalReturns([]);
      return;
    }

    setLocalReturns(returns.map((ret) => (isLegacyReturn(ret) ? convertLegacyReturnToApiReturn(ret) : ret)));
  }, [returns]);

  useEffect(() => {
    if (isCommentsDialogOpen) {
      lastCommentRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [selectedComments, isCommentsDialogOpen]);

  const sortedReturns = useMemo(() => [...localReturns].sort((a, b) => (b.ReturnNumber || "").localeCompare(a.ReturnNumber || "")), [localReturns]);

  const normalizedSearchValue = searchValue.trim().toLowerCase();
  const showSearch = sortedReturns.length > 3;

  const filteredReturns = useMemo(() => {
    if (!normalizedSearchValue) return sortedReturns;
    return sortedReturns.filter((row) => {
      const haystack = [
        row.ResellerName,
        row.ResellerId,
        row.ReturnNumber,
        getReturnMpn(row),
        row.RandmarSKU,
        row.Quantity?.toString(),
        row.WarehouseLocation,
        row.RequestDate,
        row.Status,
        row.InvoiceNumber,
        row.CreditNumber,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(normalizedSearchValue);
    });
  }, [normalizedSearchValue, sortedReturns]);

  useEffect(() => {
    setPage(0);
  }, [normalizedSearchValue]);

  const totalRows = filteredReturns.length;
  const safeRowsPerPage = Math.max(rowsPerPage, 1);
  const totalPages = Math.max(Math.ceil(totalRows / safeRowsPerPage), 1);
  const safePage = Math.min(page, totalPages - 1);
  const startRow = safePage * safeRowsPerPage;
  const endRow = Math.min(startRow + safeRowsPerPage, totalRows);

  const handlePreviousPage = useCallback(() => setPage((prev) => Math.max(prev - 1, 0)), []);
  const handleNextPage = useCallback((maxPages: number) => setPage((prev) => Math.min(prev + 1, maxPages - 1)), []);

  const parseComments = useCallback((comments: string | undefined | null): ParsedComment[] => {
    if (!comments) return [];
    const lines = comments.split(/\r?\n/).filter((line) => line.trim() !== "");

    return lines.map((line) => {
      const delimiter = " by ";
      const delimiterIndex = line.indexOf(delimiter);
      if (delimiterIndex === -1) {
        return { message: line.trim(), meta: "" };
      }
      return {
        message: line.substring(0, delimiterIndex).trim(),
        meta: line.substring(delimiterIndex + delimiter.length).trim(),
      };
    });
  }, []);

  const handleOpenCommentsDialog = useCallback(
    (returnDoc: ApiReturn) => {
      setSelectedReturn(returnDoc);
      setSelectedComments(parseComments(returnDoc.Comments));
      setCommentsDialogOpen(true);
    },
    [parseComments]
  );

  const handleCloseCommentsDialog = useCallback(() => {
    setCommentsDialogOpen(false);
    setSelectedReturn(null);
    setNewComment("");
  }, []);

  const getWarehouseNameFromCode = useCallback(
    (code?: string): string => {
      if (!code || !warehouses) {
        return "B/O";
      }
      const warehouse = warehouses.find((w) => w.WarehouseId === code);
      return warehouse?.Name || "B/O";
    },
    [warehouses]
  );

  const handleAddComment = useCallback(async () => {
    if (!newComment.trim() || !selectedReturn) return;
    const resellerId = selectedReturn.ResellerId?.trim();
    const returnNumber = selectedReturn.ReturnNumber?.trim();
    if (!resellerId || !returnNumber) {
      toast({
        variant: "destructive",
        title: "Unable to add comment",
        description: "This return is missing a reseller ID or return number.",
      });
      return;
    }

    try {
      await addComment({
        routeResellerId: resellerId,
        returnNumber,
        comment: newComment.trim(),
      }).unwrap();

      const newCommentText = `${newComment.trim()} by You just now`;
      const updatedComments = selectedReturn.Comments ? `${selectedReturn.Comments}\r\n${newCommentText}` : newCommentText;
      const updatedReturn = { ...selectedReturn, Comments: updatedComments };

      setLocalReturns((prevReturns) => {
        const returnIndex = prevReturns.findIndex((ret) => ret.ReturnNumber === selectedReturn.ReturnNumber);
        if (returnIndex === -1) return prevReturns;

        const next = [...prevReturns];
        next[returnIndex] = updatedReturn;
        return next;
      });

      setSelectedReturn(updatedReturn);
      setSelectedComments(parseComments(updatedReturn.Comments));
      setNewComment("");
      toast({ variant: "success", title: "Success", description: "Comment added successfully." });
    } catch (error) {
      console.error("Failed to add comment:", error);
      toast({ variant: "destructive", title: "Error", description: "Failed to add comment. Please try again." });
    }
  }, [addComment, newComment, parseComments, selectedReturn, toast]);

  if (!localReturns.length) {
    return (
      <div className="grid place-items-center rounded-2xl border border-dashed border-gray-100 bg-gray-50/60 p-10 text-center">
        <p className="text-[15px] text-slate-500">There are no returns.</p>
      </div>
    );
  }

  return (
    <>
      <Card className="w-full overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
        {showSearch ? (
          <div className="border-b border-gray-100 px-4 py-3">
            <div className="relative max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden="true" />
              <Input
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                placeholder="Search returns…"
                className="h-11 rounded-lg pl-10"
                aria-label="Search returns"
              />
            </div>
          </div>
        ) : null}

        {filteredReturns.length === 0 ? (
          <div className="grid place-items-center p-10 text-center">
            <p className="text-[15px] font-medium text-slate-900">No matching returns</p>
            <p className="mt-1 text-[13px] text-slate-500">Try a different search or clear the filter.</p>
            <Button type="button" variant="outline" className="mt-4 rounded-xl" onClick={() => setSearchValue("")}>
              Clear search
            </Button>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow className="h-12">
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Reseller
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Return #
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Part
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Qty
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Comments
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Location
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Status
                  </TableHead>
                  <TableHead className="whitespace-nowrap text-center text-[12px] font-semibold uppercase tracking-wide text-slate-500">
                    Request date
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredReturns.slice(startRow, endRow).map((row) => {
                  const commentsArray = parseComments(row.Comments);
                  const displayMpn = getReturnMpn(row);
                  return (
                    <TableRow key={`Return-${row.ReturnNumber}`} className="h-14 transition-colors hover:bg-slate-50">
                      <TableCell className="notranslate whitespace-nowrap text-center font-semibold text-slate-900">
                        <Link to={Path.reseller(applicationId, row.ResellerId || "")} className="hover:underline">
                          {row.ResellerName}
                        </Link>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center text-slate-900">{row.ReturnNumber}</TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        <Link to={Path.product(applicationId, row.RandmarSKU ?? undefined)} className="text-slate-900 hover:underline">
                          {displayMpn}
                        </Link>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center font-semibold text-slate-900">{row.Quantity}</TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="rounded-xl"
                          onClick={() => handleOpenCommentsDialog(row)}
                        >
                          {commentsArray.length > 0
                            ? `${commentsArray.length} ${commentsArray.length === 1 ? "Comment" : "Comments"}`
                            : "Add comment"}
                        </Button>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center text-slate-700">
                        {getWarehouseNameFromCode(row.WarehouseLocation ?? undefined)}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center">
                        <ReturnStatusPill status={row.Status} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-center text-slate-700">{(row.RequestDate || "").split("T")[0] || "—"}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {totalRows > safeRowsPerPage ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
            <p className="text-[13px] text-slate-500">
              Showing <span className="font-semibold text-slate-900">{startRow + 1}</span>–
              <span className="font-semibold text-slate-900">{endRow}</span> of{" "}
              <span className="font-semibold text-slate-900">{totalRows}</span>
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <Select
                value={String(safeRowsPerPage)}
                onValueChange={(value) => {
                  setRowsPerPage(Number.parseInt(value, 10));
                  setPage(0);
                }}
              >
                <SelectTrigger className="h-9 w-[130px] rounded-xl">
                  <SelectValue placeholder="Rows" />
                </SelectTrigger>
                <SelectContent>
                  {rowsPerPageOptions.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option} rows
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button type="button" variant="outline" size="sm" className="h-9 rounded-xl px-3" onClick={handlePreviousPage} disabled={safePage === 0}>
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-9 rounded-xl px-3"
                onClick={() => handleNextPage(totalPages)}
                disabled={safePage >= totalPages - 1}
              >
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </Card>

      <Dialog open={isCommentsDialogOpen} onOpenChange={(isOpen) => !isOpen && handleCloseCommentsDialog()}>
        <DialogContent className="sm:max-w-[900px]">
          <DialogHeader>
            <DialogTitle>Return comments for #{selectedReturn?.ReturnNumber}</DialogTitle>
            <DialogDescription>History of all comments for this return.</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[500px] w-full rounded-xl border border-slate-100 p-4">
            {selectedComments.length > 0 ? (
              selectedComments.map((comment, index) => (
                <div
                  key={`${comment.meta || "comment"}-${index}`}
                  ref={index === selectedComments.length - 1 ? lastCommentRef : null}
                  className="flex items-start gap-3 border-b border-slate-100 p-2 last:border-b-0"
                >
                  <UserCircle2 className="mt-1 h-4 w-4 flex-shrink-0 text-slate-400" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="text-[15px] font-medium text-slate-900">{comment.message}</p>
                    {comment.meta ? <p className="text-[13px] text-slate-500">{comment.meta}</p> : null}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-[15px] text-slate-500">No comments yet.</p>
            )}
          </ScrollArea>

          <div className="mt-4 space-y-3">
            <div className="flex items-start gap-2">
              <Textarea
                placeholder="Add a comment…"
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                className="min-h-[60px] flex-1"
                disabled={isAddingComment}
              />
              <Button
                type="button"
                size="icon"
                className="h-10 w-10 rounded-xl"
                onClick={handleAddComment}
                disabled={!newComment.trim() || isAddingComment || !selectedReturn?.ResellerId || !selectedReturn?.ReturnNumber}
              >
                {isAddingComment ? <InlineSpinner size="sm" decorative /> : <SendHorizontal className="h-4 w-4" aria-hidden="true" />}
              </Button>
            </div>
            {!selectedReturn?.ResellerId || !selectedReturn?.ReturnNumber ? (
              <p className="text-[13px] text-rose-600">This return is missing identifiers required to add a comment.</p>
            ) : null}
          </div>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" className="rounded-xl" onClick={handleCloseCommentsDialog}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReturnGrid;
